import { useDelete, usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { MessageSelectSlot } from "@prototypes/chat-message/MessageSelectSlot";
import classNames from "classnames";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import type { Message } from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import { getSenderRole } from "../../common/utils/message.util";
import type { Stateful } from "../../utils/react.util";
import Overlay from "../Common/Overlay";
import ChangeMessageRequest from "./ChangeMessageRequest";
import "./ChatMessage.css";
import ChatMessageActions from "./ChatMessageActions";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatMessageInner from "./ChatMessageInner";
import { useChatSelection } from "./ChatSelectionContext";
import ResendNotificationView from "./ResendNotificationView/ResendNotificationView";

export type ChatMessageProps = {
    idSelector: string;
    message: Message;
    conversation: Conversation;
    currentMember: MemberDataFirestoreRecord;
    currentAgent: Agent;
    isUnread: boolean;
    editState: Stateful<boolean>;
    notPartOfRequest: boolean;
    currentRequest: Request | undefined;
};

function ChatMessage(props: ChatMessageProps) {
    const {
        idSelector,
        message,
        conversation,
        currentMember,
        currentAgent,
        editState,
        isUnread,
        notPartOfRequest,
        currentRequest,
    } = props;

    const [resendNotificationVisible, setResendNotificationVisible] = useState(false);
    const [editTopicVisible, setEditTopicVisible] = useState(false);
    const [isCurrentlyEdited, setIsCurrentlyEdited] = editState;
    const history = useHistory();
    const sender = getSenderRole(message);

    const { selecting, isSelected, toggle } = useChatSelection();
    const selected = isSelected(message.id);
    const selectSide = sender === "member" ? "left" : "right";
    const { mutate: deleteConversationMessage } = useDelete(
        "deleteConversationMessage",
    );
    const { mutate: sanitizeMessage } = usePost("sanitizeMessage");

    return (
        <div
            id={idSelector}
            className={classNames(
                "chat-message",
                `chat-message--${sender}`,
                "group/row relative",
                {
                    "chat-message__is-edited": isCurrentlyEdited,
                    "chat-message__not-part-of-topic": notPartOfRequest,
                    "chat-message__is-unread": isUnread,
                    "mt-2 rounded-lg bg-zinc-200": selected,
                },
            )}
        >
            <div className="chat-message--spacing">
                <ChatMessageActions
                    messageBase={message}
                    currentMember={currentMember}
                    currentAgent={currentAgent}
                    conversation={conversation}
                    onEdit={() => {
                        setIsCurrentlyEdited(true);
                    }}
                    onDelete={
                        message.sentByAgent
                            ? () => {
                                  if (
                                      window.confirm(
                                          `Delete message?\n${message.message}`,
                                      )
                                  ) {
                                      deleteConversationMessage({
                                          param: {
                                              conversationId: conversation.id,
                                              messageId: message.id,
                                          },
                                      });
                                  }
                              }
                            : undefined
                    }
                    onResendNotifications={
                        message.sentByAgent
                            ? () => {
                                  setResendNotificationVisible(true);
                              }
                            : undefined
                    }
                    onEditTopic={() => {
                        setEditTopicVisible(true);
                    }}
                    onRedact={() => {
                        if (
                            !window.confirm(
                                'Redact sensitive data? This will replace the message with "Message with personal data removed" and delete any attachments sent with the message. This action cannot be undone, not even by the tech team. Are you sure?',
                            )
                        ) {
                            return;
                        }

                        void sanitizeMessage({
                            param: {
                                conversationId: conversation.id,
                                messageId: message.id,
                            },
                            json: {},
                        });
                    }}
                    disabledActions={[]}
                >
                    <ChatMessageInner
                        sender={sender}
                        messageBase={message}
                        onRequestLinkClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            history.push(`/request/${message.request}`);
                        }}
                    >
                        <ChatMessageBubble
                            message={message}
                            sender={sender}
                            currentMember={currentMember}
                            selectSlot={
                                <MessageSelectSlot
                                    side={selectSide}
                                    selected={selected}
                                    selecting={selecting}
                                    onToggle={() => toggle(message.id)}
                                />
                            }
                        />
                    </ChatMessageInner>
                </ChatMessageActions>
            </div>

            {resendNotificationVisible && (
                <Overlay
                    className="light"
                    onClose={() => {
                        setResendNotificationVisible(false);
                        return false;
                    }}
                >
                    <ResendNotificationView
                        message={message}
                        currentMember={currentMember}
                        conversationId={conversation.id}
                        close={() => {
                            setResendNotificationVisible(false);
                        }}
                    />
                </Overlay>
            )}
            {editTopicVisible && (
                <Overlay
                    className="light"
                    onClose={() => {
                        setEditTopicVisible(false);
                        return false;
                    }}
                >
                    <ChangeMessageRequest
                        messages={[message]}
                        conversationId={conversation.id}
                        member={currentMember}
                        close={() => {
                            setEditTopicVisible(false);
                        }}
                        currentRequest={currentRequest}
                    />
                </Overlay>
            )}
        </div>
    );
}

export default ChatMessage;
